import React, { useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { geodeticToENU } from '@ydrive/gs-viewer';
import * as THREE from 'three';
import { TERREBONNE_ELECTRICAL_NETWORK_INTERGEO } from '../data/electricalNetworkIntergeo';
import { mtm8ToLatLon } from '../utils/measureUtils';

interface Props {
  viewerRef: React.MutableRefObject<any>;
  visible: boolean;
  onClose: () => void;
  observerCoordinates: [number, number, number] | null;
}

// MTM8 [easting, northing, elev] → Three.js world Vector3
function mtm8ToWorld(
  easting: number,
  northing: number,
  elev: number,
  obsLat: number,
  obsLon: number,
  obsAlt: number
): THREE.Vector3 {
  const { latitude, longitude } = mtm8ToLatLon(easting, northing);
  const { east, north, up } = geodeticToENU(latitude, longitude, elev, obsLat, obsLon, obsAlt);
  const world = new THREE.Vector3(east, north, up);
  world.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
  return world;
}

// ENU origin used by the Terrebonne reconstruction, from the scene manifest.
// Prefer runtime viewer/manifest observer coordinates when available; this is
// only a fallback for single-scene loads where the manifest object is absent.
// With this origin, the cable dataset lands near the reconstruction camera.
const TERREBONNE_OBSERVER: [number, number, number] = [45.73685954736048, -73.65234375, 0];

// Thin utility-map styling: visible against the reconstruction without
// overwhelming the underlying road and buildings.
const BT_COLOR = '#38bdf8';   // sky-400
const HTA_COLOR = '#f59e0b';  // amber-500
const LINE_WIDTH_BT = 2;
const LINE_WIDTH_HTA = 2;
// Client survey elevations sit slightly above the reconstructed road surface.
// Keep the source data untouched and apply this display-only underground drop.
const CABLE_DISPLAY_VERTICAL_OFFSET = -2.0;

const ElectricalNetworkOverlay: React.FC<Props> = ({ viewerRef, visible, onClose, observerCoordinates }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const worldLines = useRef<{ points: THREE.Vector3[]; isHTA: boolean }[]>([]);
  const observerKeyRef = useRef<string>('');

  const resolveObserver = useCallback((): [number, number, number] => {
    const viewerObserver = viewerRef.current?.observerCoordinates;
    if (viewerObserver && viewerObserver.length >= 3) {
      return viewerObserver as [number, number, number];
    }
    if (observerCoordinates && observerCoordinates.length >= 3) {
      return observerCoordinates;
    }
    return TERREBONNE_OBSERVER;
  }, [observerCoordinates, viewerRef]);

  const rebuildWorldLines = useCallback((obs: [number, number, number]) => {
    const [obsLat, obsLon, obsAlt] = obs;

    const lines: { points: THREE.Vector3[]; isHTA: boolean }[] = [];

    for (const poly of TERREBONNE_ELECTRICAL_NETWORK_INTERGEO.CablesBT) {
      const pts = (poly as readonly (readonly [number, number, number])[]).map(
        ([e, n, z]) => mtm8ToWorld(e, n, z + CABLE_DISPLAY_VERTICAL_OFFSET, obsLat, obsLon, obsAlt)
      );
      lines.push({ points: pts, isHTA: false });
    }
    for (const poly of TERREBONNE_ELECTRICAL_NETWORK_INTERGEO.CablesHTA) {
      const pts = (poly as readonly (readonly [number, number, number])[]).map(
        ([e, n, z]) => mtm8ToWorld(e, n, z + CABLE_DISPLAY_VERTICAL_OFFSET, obsLat, obsLon, obsAlt)
      );
      lines.push({ points: pts, isHTA: true });
    }

    worldLines.current = lines;
    observerKeyRef.current = obs.map(v => v.toFixed(8)).join(',');
  }, []);

  // Pre-compute world positions once observer coordinates are available, and
  // rebuild if gs-viewer exposes observerCoordinates after the overlay opens.
  useEffect(() => {
    if (!visible) return;

    const rebuildIfNeeded = () => {
      const obs = resolveObserver();
      const key = obs.map(v => v.toFixed(8)).join(',');
      if (key !== observerKeyRef.current) {
        rebuildWorldLines(obs);
      }
    };

    rebuildIfNeeded();
    const interval = window.setInterval(rebuildIfNeeded, 500);
    return () => window.clearInterval(interval);
  }, [rebuildWorldLines, resolveObserver, visible]);

  // RAF draw loop
  useEffect(() => {
    if (!visible) {
      cancelAnimationFrame(rafRef.current);
      return;
    }

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);

      const canvas = canvasRef.current;
      const viewer = viewerRef.current;
      if (!canvas || !viewer) return;

      const camera: THREE.Camera | undefined = viewer.camera;
      const rendererCanvas: HTMLCanvasElement | undefined = viewer.renderer?.domElement;
      if (!camera || !rendererCanvas) return;

      const rect = rendererCanvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const targetWidth = Math.max(1, Math.round(rect.width * dpr));
      const targetHeight = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
      }
      canvas.style.left = `${rect.left}px`;
      canvas.style.top = `${rect.top}px`;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      camera.updateMatrixWorld(true);
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const cameraPosition = new THREE.Vector3();
      const cameraForward = new THREE.Vector3(0, 0, -1);
      camera.getWorldPosition(cameraPosition);
      camera.getWorldDirection(cameraForward);

      const project = (v: THREE.Vector3) => {
        const distanceAlongView = v.clone().sub(cameraPosition).dot(cameraForward);
        const ndc = v.clone().project(camera);
        const finite = Number.isFinite(ndc.x) && Number.isFinite(ndc.y) && Number.isFinite(ndc.z);
        return {
          x: (ndc.x * 0.5 + 0.5) * targetWidth,
          y: (-ndc.y * 0.5 + 0.5) * targetHeight,
          viewZ: -distanceAlongView,
          ndc,
          visible: finite && distanceAlongView > 0 && ndc.z <= 1,
        };
      };

      let projectedPoints = 0;
      let frontPoints = 0;
      let drawnSegments = 0;
      let minScreenX = Infinity;
      let minScreenY = Infinity;
      let maxScreenX = -Infinity;
      let maxScreenY = -Infinity;

      for (const line of worldLines.current) {
        if (line.points.length < 2) continue;

        const projected = line.points.map(project);
        projectedPoints += projected.length;
        frontPoints += projected.filter(p => p.visible).length;
        for (const p of projected) {
          if (!p.visible) continue;
          minScreenX = Math.min(minScreenX, p.x);
          minScreenY = Math.min(minScreenY, p.y);
          maxScreenX = Math.max(maxScreenX, p.x);
          maxScreenY = Math.max(maxScreenY, p.y);
        }

        const strokePass = (color: string, width: number, alpha: number) => {
          ctx.beginPath();
          ctx.strokeStyle = color;
          ctx.lineWidth = width * dpr;
          ctx.globalAlpha = alpha;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          // Clip each world-space segment against the camera near plane. This
          // keeps a cable continuous when one endpoint passes behind the
          // camera, instead of dropping the whole segment.
          const near = 0.01;
          for (let i = 0; i < line.points.length - 1; i += 1) {
            const a = line.points[i];
            const b = line.points[i + 1];
            const da = a.clone().sub(cameraPosition).dot(cameraForward);
            const db = b.clone().sub(cameraPosition).dot(cameraForward);
            if (da <= near && db <= near) continue;

            let start = a;
            let end = b;
            if (da <= near) {
              const t = (near - da) / (db - da);
              start = a.clone().lerp(b, t);
            } else if (db <= near) {
              const t = (near - da) / (db - da);
              end = a.clone().lerp(b, t);
            }

            const ps = project(start);
            const pe = project(end);
            if (!ps.ndc || !pe.ndc || ps.ndc.z > 1 || pe.ndc.z > 1) continue;
            ctx.moveTo(ps.x, ps.y);
            ctx.lineTo(pe.x, pe.y);
            drawnSegments += color === '#020617' ? 0 : 1;
          }
          ctx.stroke();
        };

        const width = line.isHTA ? LINE_WIDTH_HTA : LINE_WIDTH_BT;
        strokePass('#0f172a', width + 2, 0.6);
        strokePass(line.isHTA ? HTA_COLOR : BT_COLOR, width, 0.95);
      }

      ctx.globalAlpha = 1;

      const intersectsCanvas =
        frontPoints > 0 &&
        maxScreenX >= 0 &&
        minScreenX <= targetWidth &&
        maxScreenY >= 0 &&
        minScreenY <= targetHeight;

      // Do not move the network when it is outside the current camera view.
      // These are georeferenced coordinates; silently translating them into
      // view would make the overlay appear aligned while being spatially false.
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [visible, viewerRef]);

  if (!visible) return null;

  return ReactDOM.createPortal(
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          zIndex: 9998,
        }}
      />
    </>,
    document.body,
  );
};

export default ElectricalNetworkOverlay;
